import { verifyZipAndRecord } from "@/modules/Patient/PatientList/util/commonUtil"
import http from "../../../global/http"
import {
  clinicalRequestBasicType,
  getZipsType,
  getRefinementType,
  messageBody,
  UploadZips,
  UploadFileInfo
} from "./clinical.types"
import { caseManagement,wasmModule } from "@/gluelayer"
import { ZipFileList,ZipFileName } from "@/gluelayer/core/common/types"


export const getFileInfo = async ({
  orgId,
  patientId,
  caseId,
}: clinicalRequestBasicType): Promise<unknown> => {
  try {
    const { data } = await http.get(
      `/patient-mgmt/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/files`,
    )

    return data
  } catch (err) {
    return err
  }
}
export const getFileZips = async ({
  patientId,
  caseId,
  zipNames,
  orgId,
}: getZipsType) => {
  return Promise.all(
    zipNames.map((item) => {
      return http.get(
        `/patient-mgmt/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/cases/${item}.zip`,
        {
          method: "GET",
          responseType: "blob",
        },
      )
    }),
  )
}



export const downloadRefinementFile = async ({
  patientId,
  caseId,
  refinementFiles,
  orgId,
}: getRefinementType) => {
  return Promise.all(
    refinementFiles.map((item) => {
      return http.get(
        `/patient-mgmt/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/cases/${item}`,
        {
          method: "GET",
          responseType: "blob",
        },
      )
    }),
  )
}



/**
 * get treatment plan information
 * @param orgId
 * @param patientId
 * @returns: treament list
 */
export async function getCaseByPatient(orgId: string, patientId: string) {
  try {
    const { data } = await http.get(
      `/patient-mgmt/v1/org/${orgId}/patients/${patientId}/txplans`,
    )
    return data
  } catch (err) {
    return err
  }
}


export async function uploadZips({
  patientId,
  caseId,
  zips,
  orgId, 
  skipLockCheck=null,
}: UploadZips) {
  return Promise.all(
    zips.map(({ file, fileName, isRefinement }) => {
      // I don't know why check zip files here.
      fileName.includes(".zip") && verifyZipAndRecord(file, fileName, "clinical_verifyzip_event")
      const formData = new FormData()
      formData.append("attachment", file)
      formData.append("is_refinement",isRefinement?isRefinement:"0")
      skipLockCheck && formData.append('skip-lock-check', String(skipLockCheck))
      return http.post(
        `/patient-mgmt/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/cases/${fileName}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      )
    }),
  )
}


export interface DeleteCaseFilesParams {
  patientId: string | undefined
  caseId: string | undefined
  orgId: string
  fileNames:string[] // file names include postfix
}

export async function deleteFiles({
  patientId,
  caseId,
  orgId,
  fileNames,
}: DeleteCaseFilesParams){
  return Promise.all(
    fileNames.map((item) => {
      return http.delete(
        `/patient-mgmt/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/cases/${item}`,
      )
    }),
  )
}


export async function postMessage({
  patientId,
  caseId,
  orgId,
  subject,
  txName,
  content,
}: messageBody) {
  const {
    data: { results, status },
  } = await http.post(
    `/shared/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/messages`,
    { subject, txName, content },
  )
  return { result: results[0], status }
}

export async function postModiMessage({
  patientId,
  caseId,
  orgId,
  subject,
  txName,
  content,
  is_modification_requested,
}: messageBody) {
  let payload = {
    message: content,
    plan_name: txName,
    is_modification_requested,
  }
  if (is_modification_requested === 0) {
    payload = {
      message: content,
      is_modification_requested,
    }
  }
  const { data } = await http.post(
    `/shared/v1/org/${orgId}/orchestration/patients/${patientId}/txplans/${caseId}/messages/`,
    { ...payload },
  )
  return { ...data }
}

export async function updateMessage({
  patientId,
  caseId,
  orgId,
  messageId,
  ...payload
}: messageBody) {
  const { data } = await http.put(
    `/shared/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/messages/${messageId}`,
    { ...payload },
  )
  return { ...data }
}

export async function getMessage({
  patientId,
  caseId,
  orgId,
}: clinicalRequestBasicType) {
  const {
    data: { results, status },
  } = await http.get(
    `/shared/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/messages`,
  )
  return { results, status }
}

export async function getIsRead({
  patientId,
  caseId,
  orgId,
}: clinicalRequestBasicType) {
  const {
    data: { results, status },
  } = await http.get(
    `/shared/v1/org/${orgId}/patients/${patientId}/txplans/${caseId}/messages?unreadOnly=true`,
  )
  return { results, status }
}

export async function getCaseInfo({
  patientId,
  caseId,
  orgId,
}: clinicalRequestBasicType) {
  const {
    data: { result, status },
  } = await http.get(
    `/patient-mgmt/v2/org/${orgId}/patients/${patientId}/txplans/${caseId}`,
  )
  return { result, status }
}

export async function lockCaseSever({
  patientId,
  caseId,
  orgId,
}: clinicalRequestBasicType) {
  const {
    data: { result, status },
  } = await http.post(
    `/patient-mgmt/v2/org/${orgId}/patients/${patientId}/txplans/${caseId}/lock`,
  )
  return { result, status }
}

export async function unlockCaseSever({
  patientId,
  caseId,
  orgId,
  isForceUnlock
}: clinicalRequestBasicType) {
  const forceUnlockParam = isForceUnlock ? '?force_unlock_user=1' : '';
  const url = `/patient-mgmt/v2/org/${orgId}/patients/${patientId}/txplans/${caseId}/unlock${forceUnlockParam}`
  const {
    data: { result, status },
  } = await http.post(url)
  return { result, status }
}

export async function forcelockCaseSever({
  patientId,
  caseId,
  orgId,
  isForceModify
}: clinicalRequestBasicType) {
  const payload = isForceModify ? { "force_modify": true } : { "force-unlock-first": true }
  const {
    data: { result, status },
  } = await http.post(
    `/patient-mgmt/v2/org/${orgId}/patients/${patientId}/txplans/${caseId}/lock`,
    payload,
  )
  return { result, status }
}
